'use client';
import React, { useState, useMemo } from 'react';
import AppLayout from '@/components/AppLayout';
import { Users, Clock, CheckCircle, AlertTriangle, BarChart2, Activity, TrendingUp, ChevronDown, ChevronUp, RefreshCw, Target, Download, Filter, UserCheck, AlertCircle, Layers, Timer, Award, RotateCcw, Flag, ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, AreaChart, Area, CartesianGrid, LineChart, Line } from 'recharts';
import Icon from '@/components/ui/AppIcon';


// ─── Types ────────────────────────────────────────────────────────────────────

type ActiveTab = 'overview' | 'officers' | 'pipeline' | 'overrides' | 'workload';
type TimePeriod = '7d' | '30d' | '90d';

interface OfficerStat {
  id: string;
  name: string;
  role: string;
  stage: string;
  stageCode: string;
  processed: number;
  approved: number;
  rejected: number;
  overrides: number;
  avgProcessingHrs: number;
  pendingQueue: number;
  approvalRate: number;
  status: 'active' | 'idle' | 'overloaded';
  lastActivity: string;
  county: string;
}

interface StageMetric {
  stage: string;
  code: string;
  name: string;
  color: string;
  inQueue: number;
  processed: number;
  approved: number;
  rejected: number;
  overrides: number;
  avgHrs: number;
  slaHrs: number;
  approvalRate: number;
  bottleneck: boolean;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const STAGE_COLORS: Record<string, string> = {
  S1: '#0ea5e9',
  S2: '#8b5cf6',
  S3: '#f59e0b',
  S4: '#10b981',
  S5: '#ef4444',
  S6: '#f97316',
  S7: '#06b6d4',
};

const stageMetrics: StageMetric[] = [
  { stage: 'S1', code: 'S1', name: 'Property Registration', color: '#0ea5e9', inQueue: 142, processed: 1840, approved: 1692, rejected: 148, overrides: 23, avgHrs: 4.2, slaHrs: 8, approvalRate: 91.9, bottleneck: false },
  { stage: 'S2', code: 'S2', name: 'Parcel Delineation', color: '#8b5cf6', inQueue: 98, processed: 1692, approved: 1541, rejected: 151, overrides: 41, avgHrs: 6.8, slaHrs: 12, approvalRate: 91.1, bottleneck: false },
  { stage: 'S3', code: 'S3', name: 'Ownership Verification', color: '#f59e0b', inQueue: 217, processed: 1541, approved: 1289, rejected: 252, overrides: 87, avgHrs: 18.4, slaHrs: 24, approvalRate: 83.6, bottleneck: true },
  { stage: 'S4', code: 'S4', name: 'Valuation Admin', color: '#10b981', inQueue: 183, processed: 1289, approved: 1198, rejected: 91, overrides: 62, avgHrs: 9.1, slaHrs: 16, approvalRate: 92.9, bottleneck: false },
  { stage: 'S5', code: 'S5', name: 'Tax Assessment', color: '#ef4444', inQueue: 156, processed: 1198, approved: 1087, rejected: 111, overrides: 34, avgHrs: 5.3, slaHrs: 8, approvalRate: 90.7, bottleneck: false },
  { stage: 'S6', code: 'S6', name: 'Billing & Dispatch', color: '#f97316', inQueue: 89, processed: 1087, approved: 1021, rejected: 66, overrides: 18, avgHrs: 3.1, slaHrs: 4, approvalRate: 93.9, bottleneck: false },
  { stage: 'S7', code: 'S7', name: 'Collections & Treasury', color: '#06b6d4', inQueue: 64, processed: 1021, approved: 978, rejected: 43, overrides: 11, avgHrs: 2.4, slaHrs: 4, approvalRate: 95.8, bottleneck: false },
];

const officerStats: OfficerStat[] = [
  { id: 'O1', name: 'James Kollie', role: 'Registration Officer', stage: 'Property Registration', stageCode: 'S1', processed: 312, approved: 289, rejected: 23, overrides: 4, avgProcessingHrs: 3.8, pendingQueue: 18, approvalRate: 92.6, status: 'active', lastActivity: '12 min ago', county: 'Montserrado' },
  { id: 'O2', name: 'Mary Sumo', role: 'GIS Analyst', stage: 'Parcel Delineation', stageCode: 'S2', processed: 278, approved: 251, rejected: 27, overrides: 9, avgProcessingHrs: 6.2, pendingQueue: 31, approvalRate: 90.3, status: 'active', lastActivity: '5 min ago', county: 'Montserrado' },
  { id: 'O3', name: 'David Flomo', role: 'Verification Officer', stage: 'Ownership Verification', stageCode: 'S3', processed: 198, approved: 162, rejected: 36, overrides: 22, avgProcessingHrs: 19.1, pendingQueue: 67, approvalRate: 81.8, status: 'overloaded', lastActivity: '2 min ago', county: 'Margibi' },
  { id: 'O4', name: 'Agnes Kpaan', role: 'Verification Officer', stage: 'Ownership Verification', stageCode: 'S3', processed: 187, approved: 156, rejected: 31, overrides: 18, avgProcessingHrs: 17.8, pendingQueue: 58, approvalRate: 83.4, status: 'overloaded', lastActivity: '8 min ago', county: 'Bong' },
  { id: 'O5', name: 'Samuel Weah', role: 'Valuation Analyst', stage: 'Valuation Admin', stageCode: 'S4', processed: 241, approved: 224, rejected: 17, overrides: 11, avgProcessingHrs: 8.7, pendingQueue: 42, approvalRate: 92.9, status: 'active', lastActivity: '18 min ago', county: 'Nimba' },
  { id: 'O6', name: 'Ruth Togba', role: 'Assessment Officer', stage: 'Tax Assessment', stageCode: 'S5', processed: 267, approved: 244, rejected: 23, overrides: 8, avgProcessingHrs: 5.1, pendingQueue: 29, approvalRate: 91.4, status: 'active', lastActivity: '3 min ago', county: 'Montserrado' },
  { id: 'O7', name: 'Emmanuel Doe', role: 'Billing Officer', stage: 'Billing & Dispatch', stageCode: 'S6', processed: 298, approved: 281, rejected: 17, overrides: 5, avgProcessingHrs: 2.9, pendingQueue: 14, approvalRate: 94.3, status: 'active', lastActivity: '7 min ago', county: 'Montserrado' },
  { id: 'O8', name: 'Patience Nimely', role: 'Collections Officer', stage: 'Collections & Treasury', stageCode: 'S7', processed: 321, approved: 308, rejected: 13, overrides: 3, avgProcessingHrs: 2.2, pendingQueue: 9, approvalRate: 95.9, status: 'active', lastActivity: '1 min ago', county: 'Montserrado' },
  { id: 'O9', name: 'Thomas Konneh', role: 'GIS Analyst', stage: 'Parcel Delineation', stageCode: 'S2', processed: 189, approved: 171, rejected: 18, overrides: 7, avgProcessingHrs: 7.1, pendingQueue: 22, approvalRate: 90.5, status: 'idle', lastActivity: '2 hrs ago', county: 'Lofa' },
  { id: 'O10', name: 'Cecelia Mulbah', role: 'Registration Officer', stage: 'Property Registration', stageCode: 'S1', processed: 276, approved: 254, rejected: 22, overrides: 6, avgProcessingHrs: 4.4, pendingQueue: 24, approvalRate: 92.0, status: 'active', lastActivity: '22 min ago', county: 'Grand Bassa' },
];

const processingTimeTrend = [
  { day: 'Mon', S1: 4.1, S2: 6.5, S3: 17.2, S4: 8.9, S5: 5.4, S6: 3.2, S7: 2.5 },
  { day: 'Tue', S1: 3.9, S2: 7.1, S3: 19.4, S4: 9.2, S5: 5.1, S6: 3.0, S7: 2.3 },
  { day: 'Wed', S1: 4.4, S2: 6.8, S3: 20.1, S4: 8.7, S5: 5.6, S6: 3.4, S7: 2.6 },
  { day: 'Thu', S1: 4.0, S2: 6.2, S3: 18.8, S4: 9.0, S5: 5.2, S6: 2.9, S7: 2.2 },
  { day: 'Fri', S1: 4.3, S2: 7.0, S3: 17.9, S4: 8.5, S5: 5.3, S6: 3.1, S7: 2.4 },
  { day: 'Sat', S1: 3.8, S2: 6.4, S3: 16.5, S4: 8.2, S5: 4.9, S6: 2.8, S7: 2.1 },
  { day: 'Sun', S1: 3.6, S2: 6.1, S3: 15.8, S4: 7.9, S5: 4.7, S6: 2.6, S7: 2.0 },
];

const overrideTrend = [
  { week: 'W1', S1: 3, S2: 5, S3: 14, S4: 9, S5: 5, S6: 2, S7: 1 },
  { week: 'W2', S1: 4, S2: 7, S3: 18, S4: 11, S5: 6, S6: 3, S7: 2 },
  { week: 'W3', S1: 5, S2: 9, S3: 22, S4: 14, S5: 8, S6: 4, S7: 3 },
  { week: 'W4', S1: 6, S2: 10, S3: 25, S4: 16, S5: 9, S6: 5, S7: 3 },
];

const approvalRateTrend = [
  { month: 'Jan', rate: 88.2 },
  { month: 'Feb', rate: 89.1 },
  { month: 'Mar', rate: 87.8 },
  { month: 'Apr', rate: 90.4 },
  { month: 'May', rate: 91.2 },
  { month: 'Jun', rate: 90.8 },
  { month: 'Jul', rate: 91.7 },
];

const workloadByCounty = [
  { county: 'Montserrado', S1: 48, S2: 41, S3: 62, S4: 38, S5: 44, S6: 29, S7: 21 },
  { county: 'Nimba', S1: 22, S2: 18, S3: 31, S4: 24, S5: 19, S6: 14, S7: 10 },
  { county: 'Bong', S1: 18, S2: 15, S3: 28, S4: 20, S5: 16, S6: 11, S7: 8 },
  { county: 'Margibi', S1: 16, S2: 12, S3: 24, S4: 18, S5: 14, S6: 9, S7: 7 },
  { county: 'Grand Bassa', S1: 14, S2: 10, S3: 19, S4: 14, S5: 11, S6: 8, S7: 6 },
  { county: 'Lofa', S1: 12, S2: 9, S3: 16, S4: 12, S5: 9, S6: 6, S7: 5 },
];

const overrideReasons = [
  { reason: 'Document Discrepancy', count: 87, pct: 32.1 },
  { reason: 'Boundary Dispute', count: 54, pct: 19.9 },
  { reason: 'Valuation Disagreement', count: 48, pct: 17.7 },
  { reason: 'Ownership Conflict', count: 41, pct: 15.1 },
  { reason: 'System Error Correction', count: 28, pct: 10.3 },
  { reason: 'Policy Exception', count: 13, pct: 4.8 },
];

const PIE_COLORS = ['#0ea5e9', '#8b5cf6', '#f59e0b', '#10b981', '#ef4444', '#f97316'];

// ─── Sub-components ────────────────────────────────────────────────────────────

const KPICard = ({
  label, value, sub, icon: Icon, trend, trendDir, color
}: {
  label: string; value: string; sub?: string; icon: React.ElementType;
  trend?: string; trendDir?: 'up' | 'down' | 'neutral'; color: string;
}) => (
  <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4 flex flex-col gap-2">
    <div className="flex items-center justify-between">
      <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">{label}</span>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center`} style={{ backgroundColor: `${color}20` }}>
        <Icon size={16} style={{ color }} />
      </div>
    </div>
    <div className="text-2xl font-bold text-white">{value}</div>
    {sub && <div className="text-xs text-slate-400">{sub}</div>}
    {trend && (
      <div className={`flex items-center gap-1 text-xs font-medium ${
        trendDir === 'up' ? 'text-emerald-400' : trendDir === 'down' ? 'text-red-400' : 'text-slate-400'
      }`}>
        {trendDir === 'up' ? <ArrowUpRight size={12} /> : trendDir === 'down' ? <ArrowDownRight size={12} /> : <Minus size={12} />}
        {trend}
      </div>
    )}
  </div>
);

const StageBadge = ({ code, color }: { code: string; color: string }) => (
  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold" style={{ backgroundColor: `${color}20`, color }}>
    {code}
  </span>
);

const StatusDot = ({ status }: { status: 'active' | 'idle' | 'overloaded' }) => {
  const map = { active: 'bg-emerald-400', idle: 'bg-slate-400', overloaded: 'bg-red-400' };
  return <span className={`inline-block w-2 h-2 rounded-full ${map[status]}`} />;
};

// ─── Custom Tooltip ────────────────────────────────────────────────────────────

const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: { name: string; value: number; color: string }[]; label?: string }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-800 border border-slate-600 rounded-lg p-3 shadow-xl text-xs">
      <p className="text-slate-300 font-semibold mb-1">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
          <span className="text-slate-400">{p.name}:</span>
          <span className="text-white font-medium">{p.value}</span>
        </div>
      ))}
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────

export default function OperationsOverviewClient() {
  const [activeTab, setActiveTab] = useState<ActiveTab>('overview');
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('30d');
  const [stageFilter, setStageFilter] = useState<string>('all');
  const [sortOfficer, setSortOfficer] = useState<keyof OfficerStat>('processed');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [expandedStage, setExpandedStage] = useState<string | null>(null);

  const totalProcessed = stageMetrics[0].processed;
  const totalApproved = stageMetrics[6].approved;
  const totalOverrides = stageMetrics.reduce((s, m) => s + m.overrides, 0);
  const overallApprovalRate = ((totalApproved / totalProcessed) * 100).toFixed(1);
  const avgProcessingHrs = (stageMetrics.reduce((s, m) => s + m.avgHrs, 0) / stageMetrics.length).toFixed(1);
  const bottleneckStage = stageMetrics.find(m => m.bottleneck);

  const sortedOfficers = useMemo(() => {
    const filtered = stageFilter === 'all' ? officerStats : officerStats.filter(o => o.stageCode === stageFilter);
    return [...filtered].sort((a, b) => {
      const av = a[sortOfficer] as number;
      const bv = b[sortOfficer] as number;
      return sortDir === 'desc' ? bv - av : av - bv;
    });
  }, [stageFilter, sortOfficer, sortDir]);

  const handleSort = (col: keyof OfficerStat) => {
    if (sortOfficer === col) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortOfficer(col); setSortDir('desc'); }
  };

  const tabs: { id: ActiveTab; label: string; icon: React.ElementType }[] = [
    { id: 'overview', label: 'Overview', icon: BarChart2 },
    { id: 'officers', label: 'Officer Activity', icon: Users },
    { id: 'pipeline', label: 'Pipeline Stages', icon: Layers },
    { id: 'overrides', label: 'Override Analysis', icon: RotateCcw },
    { id: 'workload', label: 'Workload Distribution', icon: Target },
  ];

  return (
    <AppLayout>
      <div className="min-h-screen bg-slate-900 text-white">
        {/* ── Header ── */}
        <div className="border-b border-slate-700/60 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-20">
          <div className="px-6 py-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
              <div>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-indigo-500/20 flex items-center justify-center">
                    <Activity size={18} className="text-indigo-400" />
                  </div>
                  <div>
                    <h1 className="text-lg font-bold text-white leading-tight">Operations Overview Dashboard</h1>
                    <p className="text-xs text-slate-400">7-Stage Pipeline · Officer Activity · Approval Rates · Processing Time · Overrides</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1 bg-slate-800 border border-slate-700 rounded-lg p-1">
                  {(['7d', '30d', '90d'] as TimePeriod[]).map(p => (
                    <button key={p} onClick={() => setTimePeriod(p)}
                      className={`px-3 py-1 rounded text-xs font-medium transition-colors ${timePeriod === p ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'}`}>
                      {p}
                    </button>
                  ))}
                </div>
                <button className="flex items-center gap-2 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-xs text-slate-300 hover:text-white hover:border-slate-500 transition-colors">
                  <Download size={13} /> Export
                </button>
                <button className="flex items-center gap-2 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-xs text-slate-300 hover:text-white hover:border-slate-500 transition-colors">
                  <RefreshCw size={13} /> Refresh
                </button>
              </div>
            </div>

            {/* ── Tabs ── */}
            <div className="flex gap-1 overflow-x-auto scrollbar-thin pb-1">
              {tabs.map(t => (
                <button key={t.id} onClick={() => setActiveTab(t.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors flex-shrink-0 whitespace-nowrap ${
                    activeTab === t.id ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-800'
                  }`}>
                  <t.icon size={14} />
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="px-6 py-6 space-y-6">

          {/* ══════════════════════════════════════════════════════════════════
              TAB: OVERVIEW
          ══════════════════════════════════════════════════════════════════ */}
          {activeTab === 'overview' && (
            <>
              {/* KPI Strip */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                <KPICard label="Total Processed" value={totalProcessed.toLocaleString()} sub="Entered pipeline (S1)" icon={Layers} trend="+8.3% vs last period" trendDir="up" color="#0ea5e9" />
                <KPICard label="End-to-End Approved" value={totalApproved.toLocaleString()} sub="Reached S7 completion" icon={CheckCircle} trend="+5.1% vs last period" trendDir="up" color="#10b981" />
                <KPICard label="Overall Approval Rate" value={`${overallApprovalRate}%`} sub="S1 → S7 funnel" icon={Award} trend="+1.2pp vs last period" trendDir="up" color="#8b5cf6" />
                <KPICard label="Avg Processing Time" value={`${avgProcessingHrs}h`} sub="Mean across all stages" icon={Clock} trend="-0.4h vs last period" trendDir="up" color="#f59e0b" />
                <KPICard label="Total Overrides" value={totalOverrides.toString()} sub="Manual interventions" icon={RotateCcw} trend="+12 vs last period" trendDir="down" color="#ef4444" />
                <KPICard label="Active Officers" value={officerStats.filter(o => o.status === 'active').length.toString()} sub={`${officerStats.filter(o => o.status === 'overloaded').length} overloaded`} icon={Users} trend="2 overloaded" trendDir="down" color="#f97316" />
              </div>

              {/* Bottleneck Alert */}
              {bottleneckStage && (
                <div className="flex items-center gap-3 bg-amber-500/10 border border-amber-500/30 rounded-xl px-5 py-3">
                  <AlertTriangle size={16} className="text-amber-400 flex-shrink-0" />
                  <div className="flex-1 text-sm">
                    <span className="text-amber-300 font-semibold">Pipeline Bottleneck Detected — </span>
                    <span className="text-slate-300">{bottleneckStage.name} ({bottleneckStage.code}) is averaging <strong className="text-amber-300">{bottleneckStage.avgHrs}h</strong> vs {bottleneckStage.slaHrs}h SLA with <strong className="text-amber-300">{bottleneckStage.inQueue} items</strong> in queue. Recommend reassigning 2 officers from S6.</span>
                  </div>
                  <StageBadge code={bottleneckStage.code} color={STAGE_COLORS[bottleneckStage.code]} />
                </div>
              )}

              {/* Charts Row 1 */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Pipeline Funnel */}
                <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                    <Layers size={14} className="text-indigo-400" /> Pipeline Funnel — Processed vs Approved
                  </h3>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={stageMetrics} layout="vertical" barCategoryGap={8}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
                      <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="code" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} width={28} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="processed" name="Processed" fill="#334155" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="approved" name="Approved" radius={[0, 4, 4, 0]}>
                        {stageMetrics.map((s, i) => (
                          <Cell key={i} fill={s.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Approval Rate Trend */}
                <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                    <TrendingUp size={14} className="text-emerald-400" /> Overall Approval Rate Trend
                  </h3>
                  <ResponsiveContainer width="100%" height={240}>
                    <AreaChart data={approvalRateTrend}>
                      <defs>
                        <linearGradient id="approvalGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="month" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis domain={[85, 95]} tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Area type="monotone" dataKey="rate" name="Approval Rate %" stroke="#10b981" strokeWidth={2} fill="url(#approvalGrad)" dot={{ fill: '#10b981', r: 4 }} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Charts Row 2 */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Stage Approval Rates */}
                <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                    <CheckCircle size={14} className="text-sky-400" /> Approval Rate by Stage
                  </h3>
                  <div className="space-y-3">
                    {stageMetrics.map(s => (
                      <div key={s.code}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <StageBadge code={s.code} color={s.color} />
                            <span className="text-xs text-slate-300 truncate max-w-[110px]">{s.name}</span>
                          </div>
                          <span className="text-xs font-bold" style={{ color: s.approvalRate >= 90 ? '#10b981' : s.approvalRate >= 85 ? '#f59e0b' : '#ef4444' }}>
                            {s.approvalRate}%
                          </span>
                        </div>
                        <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all" style={{ width: `${s.approvalRate}%`, backgroundColor: s.color }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Avg Processing Time per Stage */}
                <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                    <Timer size={14} className="text-amber-400" /> Avg Processing Time vs SLA
                  </h3>
                  <div className="space-y-3">
                    {stageMetrics.map(s => (
                      <div key={s.code}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <StageBadge code={s.code} color={s.color} />
                            <span className="text-xs text-slate-300">{s.avgHrs}h</span>
                          </div>
                          <span className={`text-xs font-medium ${s.avgHrs > s.slaHrs ? 'text-red-400' : 'text-emerald-400'}`}>
                            SLA: {s.slaHrs}h {s.avgHrs > s.slaHrs ? '⚠' : '✓'}
                          </span>
                        </div>
                        <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all" style={{
                            width: `${Math.min((s.avgHrs / s.slaHrs) * 100, 100)}%`,
                            backgroundColor: s.avgHrs > s.slaHrs ? '#ef4444' : s.color
                          }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Override Distribution Pie */}
                <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                    <RotateCcw size={14} className="text-red-400" /> Override Distribution by Stage
                  </h3>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={stageMetrics} dataKey="overrides" nameKey="code" cx="50%" cy="50%" outerRadius={70} label={({ code, overrides }) => `${code}:${overrides}`} labelLine={false}>
                        {stageMetrics.map((s, i) => <Cell key={i} fill={s.color} />)}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="grid grid-cols-4 gap-1 mt-2">
                    {stageMetrics.map(s => (
                      <div key={s.code} className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
                        <span className="text-xs text-slate-400">{s.code}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ══════════════════════════════════════════════════════════════════
              TAB: OFFICER ACTIVITY
          ══════════════════════════════════════════════════════════════════ */}
          {activeTab === 'officers' && (
            <>
              {/* Officer KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KPICard label="Total Officers" value={officerStats.length.toString()} sub="Across all stages" icon={Users} color="#0ea5e9" />
                <KPICard label="Active Now" value={officerStats.filter(o => o.status === 'active').length.toString()} sub="Processing items" icon={UserCheck} trend="2 overloaded" trendDir="down" color="#10b981" />
                <KPICard label="Overloaded" value={officerStats.filter(o => o.status === 'overloaded').length.toString()} sub="Queue > 50 items" icon={AlertCircle} trend="Needs rebalancing" trendDir="down" color="#ef4444" />
                <KPICard label="Avg Approval Rate" value={`${(officerStats.reduce((s, o) => s + o.approvalRate, 0) / officerStats.length).toFixed(1)}%`} sub="All officers" icon={Award} color="#8b5cf6" />
              </div>

              {/* Filters */}
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-xs text-slate-400 flex items-center gap-1"><Filter size={12} /> Filter by Stage:</span>
                {['all', ...stageMetrics.map(s => s.code)].map(code => (
                  <button key={code} onClick={() => setStageFilter(code)}
                    className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors border ${
                      stageFilter === code
                        ? 'bg-indigo-600 border-indigo-500 text-white' :'bg-slate-800 border-slate-700 text-slate-400 hover:text-white'
                    }`}
                    style={stageFilter === code && code !== 'all' ? { backgroundColor: STAGE_COLORS[code], borderColor: STAGE_COLORS[code] } : {}}>
                    {code === 'all' ? 'All Stages' : code}
                  </button>
                ))}
              </div>

              {/* Officer Table */}
              <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-700/60">
                        {[
                          { label: 'Officer', key: 'name' as keyof OfficerStat },
                          { label: 'Stage', key: 'stageCode' as keyof OfficerStat },
                          { label: 'Processed', key: 'processed' as keyof OfficerStat },
                          { label: 'Approved', key: 'approved' as keyof OfficerStat },
                          { label: 'Rejected', key: 'rejected' as keyof OfficerStat },
                          { label: 'Overrides', key: 'overrides' as keyof OfficerStat },
                          { label: 'Approval %', key: 'approvalRate' as keyof OfficerStat },
                          { label: 'Avg Time', key: 'avgProcessingHrs' as keyof OfficerStat },
                          { label: 'Queue', key: 'pendingQueue' as keyof OfficerStat },
                          { label: 'Status', key: 'status' as keyof OfficerStat },
                        ].map(col => (
                          <th key={col.key} onClick={() => handleSort(col.key)}
                            className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider cursor-pointer hover:text-white select-none">
                            <div className="flex items-center gap-1">
                              {col.label}
                              {sortOfficer === col.key && (sortDir === 'desc' ? <ChevronDown size={12} /> : <ChevronUp size={12} />)}
                            </div>
                          </th>
                        ))}
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Last Active</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700/40">
                      {sortedOfficers.map(o => (
                        <tr key={o.id} className="hover:bg-slate-700/30 transition-colors">
                          <td className="px-4 py-3">
                            <div>
                              <div className="font-medium text-white text-sm">{o.name}</div>
                              <div className="text-xs text-slate-400">{o.role} · {o.county}</div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <StageBadge code={o.stageCode} color={STAGE_COLORS[o.stageCode]} />
                          </td>
                          <td className="px-4 py-3 text-white font-medium">{o.processed.toLocaleString()}</td>
                          <td className="px-4 py-3 text-emerald-400 font-medium">{o.approved.toLocaleString()}</td>
                          <td className="px-4 py-3 text-red-400 font-medium">{o.rejected}</td>
                          <td className="px-4 py-3">
                            <span className={`font-medium ${o.overrides > 15 ? 'text-red-400' : o.overrides > 8 ? 'text-amber-400' : 'text-slate-300'}`}>
                              {o.overrides}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`font-bold ${o.approvalRate >= 90 ? 'text-emerald-400' : o.approvalRate >= 85 ? 'text-amber-400' : 'text-red-400'}`}>
                              {o.approvalRate}%
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-300">{o.avgProcessingHrs}h</td>
                          <td className="px-4 py-3">
                            <span className={`font-medium ${o.pendingQueue > 50 ? 'text-red-400' : o.pendingQueue > 25 ? 'text-amber-400' : 'text-slate-300'}`}>
                              {o.pendingQueue}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <StatusDot status={o.status} />
                              <span className={`text-xs capitalize ${o.status === 'active' ? 'text-emerald-400' : o.status === 'overloaded' ? 'text-red-400' : 'text-slate-400'}`}>
                                {o.status}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-400">{o.lastActivity}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Officer Performance Chart */}
              <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                  <BarChart2 size={14} className="text-indigo-400" /> Officer Processed vs Approved vs Overrides
                </h3>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={sortedOfficers.slice(0, 8)} barCategoryGap="20%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 10 }} axisLine={false} tickLine={false}
                      tickFormatter={v => v.split(' ')[0]} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11, color: '#94a3b8' }} />
                    <Bar dataKey="processed" name="Processed" fill="#334155" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="approved" name="Approved" fill="#10b981" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="overrides" name="Overrides" fill="#ef4444" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          )}

          {/* ══════════════════════════════════════════════════════════════════
              TAB: PIPELINE STAGES
          ══════════════════════════════════════════════════════════════════ */}
          {activeTab === 'pipeline' && (
            <>
              {/* Stage Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {stageMetrics.map(s => (
                  <div key={s.code}
                    className={`bg-slate-800/60 border rounded-xl overflow-hidden transition-all cursor-pointer ${
                      s.bottleneck ? 'border-amber-500/50' : 'border-slate-700/50 hover:border-slate-600'
                    }`}
                    onClick={() => setExpandedStage(expandedStage === s.code ? null : s.code)}>
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <StageBadge code={s.code} color={s.color} />
                          <span className="text-sm font-semibold text-white">{s.name}</span>
                          {s.bottleneck && <span className="text-xs bg-amber-500/20 text-amber-400 border border-amber-500/30 px-2 py-0.5 rounded-full">Bottleneck</span>}
                        </div>
                        {expandedStage === s.code ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
                      </div>

                      <div className="grid grid-cols-3 gap-3 mb-3">
                        <div className="text-center">
                          <div className="text-lg font-bold text-white">{s.inQueue}</div>
                          <div className="text-xs text-slate-400">In Queue</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold" style={{ color: s.approvalRate >= 90 ? '#10b981' : s.approvalRate >= 85 ? '#f59e0b' : '#ef4444' }}>
                            {s.approvalRate}%
                          </div>
                          <div className="text-xs text-slate-400">Approval</div>
                        </div>
                        <div className="text-center">
                          <div className={`text-lg font-bold ${s.avgHrs > s.slaHrs ? 'text-red-400' : 'text-emerald-400'}`}>{s.avgHrs}h</div>
                          <div className="text-xs text-slate-400">Avg Time</div>
                        </div>
                      </div>

                      {/* Progress bar */}
                      <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${s.approvalRate}%`, backgroundColor: s.color }} />
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {expandedStage === s.code && (
                      <div className="border-t border-slate-700/60 p-4 bg-slate-900/40">
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <span className="text-slate-400">Processed</span>
                              <span className="text-white font-medium">{s.processed.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-400">Approved</span>
                              <span className="text-emerald-400 font-medium">{s.approved.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-400">Rejected</span>
                              <span className="text-red-400 font-medium">{s.rejected}</span>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <span className="text-slate-400">Overrides</span>
                              <span className="text-amber-400 font-medium">{s.overrides}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-400">SLA Target</span>
                              <span className="text-slate-300 font-medium">{s.slaHrs}h</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-400">SLA Status</span>
                              <span className={`font-medium ${s.avgHrs > s.slaHrs ? 'text-red-400' : 'text-emerald-400'}`}>
                                {s.avgHrs > s.slaHrs ? `+${(s.avgHrs - s.slaHrs).toFixed(1)}h over` : 'Within SLA'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Processing Time Trend */}
              <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                  <Timer size={14} className="text-amber-400" /> Processing Time Trend by Stage (Hours) — Last 7 Days
                </h3>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={processingTimeTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="day" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11, color: '#94a3b8' }} />
                    {stageMetrics.map(s => (
                      <Line key={s.code} type="monotone" dataKey={s.code} name={s.code} stroke={s.color} strokeWidth={2} dot={false} />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </>
          )}

          {/* ══════════════════════════════════════════════════════════════════
              TAB: OVERRIDE ANALYSIS
          ══════════════════════════════════════════════════════════════════ */}
          {activeTab === 'overrides' && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KPICard label="Total Overrides" value={totalOverrides.toString()} sub="All stages combined" icon={RotateCcw} trend="+12 this period" trendDir="down" color="#ef4444" />
                <KPICard label="Override Rate" value={`${((totalOverrides / totalProcessed) * 100).toFixed(1)}%`} sub="Of all processed items" icon={Flag} color="#f97316" />
                <KPICard label="Highest Stage" value="S3" sub="87 overrides (32.1%)" icon={AlertTriangle} color="#f59e0b" />
                <KPICard label="Top Reason" value="Doc Discrepancy" sub="87 cases · 32.1%" icon={AlertCircle} color="#8b5cf6" />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Override by Stage Bar */}
                <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                    <RotateCcw size={14} className="text-red-400" /> Override Count by Stage
                  </h3>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={stageMetrics} barCategoryGap="30%">
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="code" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="overrides" name="Overrides" radius={[4, 4, 0, 0]}>
                        {stageMetrics.map((s, i) => <Cell key={i} fill={s.color} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Override Reasons */}
                <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                    <Flag size={14} className="text-amber-400" /> Override Reasons Breakdown
                  </h3>
                  <div className="space-y-3">
                    {overrideReasons.map((r, i) => (
                      <div key={i}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-slate-300">{r.reason}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-400">{r.count}</span>
                            <span className="text-xs font-bold text-white">{r.pct}%</span>
                          </div>
                        </div>
                        <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all" style={{ width: `${r.pct}%`, backgroundColor: PIE_COLORS[i] }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Override Trend by Stage */}
              <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                  <TrendingUp size={14} className="text-indigo-400" /> Override Frequency Trend — Weekly (Last 4 Weeks)
                </h3>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={overrideTrend} barCategoryGap="20%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="week" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11, color: '#94a3b8' }} />
                    {stageMetrics.map(s => (
                      <Bar key={s.code} dataKey={s.code} name={s.code} fill={s.color} stackId="a" />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Officers with highest overrides */}
              <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                  <Users size={14} className="text-red-400" /> Officers with Highest Override Frequency
                </h3>
                <div className="space-y-2">
                  {[...officerStats].sort((a, b) => b.overrides - a.overrides).slice(0, 6).map(o => (
                    <div key={o.id} className="flex items-center gap-3 p-3 bg-slate-900/40 rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-white">{o.name}</span>
                          <StageBadge code={o.stageCode} color={STAGE_COLORS[o.stageCode]} />
                        </div>
                        <div className="text-xs text-slate-400">{o.role} · {o.county}</div>
                      </div>
                      <div className="text-right">
                        <div className={`text-sm font-bold ${o.overrides > 15 ? 'text-red-400' : o.overrides > 8 ? 'text-amber-400' : 'text-slate-300'}`}>
                          {o.overrides} overrides
                        </div>
                        <div className="text-xs text-slate-400">{((o.overrides / o.processed) * 100).toFixed(1)}% of processed</div>
                      </div>
                      <div className="w-24">
                        <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                          <div className="h-full rounded-full bg-red-500" style={{ width: `${Math.min((o.overrides / 25) * 100, 100)}%` }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* ══════════════════════════════════════════════════════════════════
              TAB: WORKLOAD DISTRIBUTION
          ══════════════════════════════════════════════════════════════════ */}
          {activeTab === 'workload' && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KPICard label="Total Queue (All Stages)" value={stageMetrics.reduce((s, m) => s + m.inQueue, 0).toString()} sub="Items pending processing" icon={Layers} color="#0ea5e9" />
                <KPICard label="Heaviest Stage" value="S3" sub="217 items in queue" icon={AlertTriangle} trend="Bottleneck risk" trendDir="down" color="#f59e0b" />
                <KPICard label="Lightest Stage" value="S7" sub="64 items in queue" icon={CheckCircle} color="#10b981" />
                <KPICard label="Officers Overloaded" value={officerStats.filter(o => o.status === 'overloaded').length.toString()} sub="Queue > 50 items" icon={AlertCircle} color="#ef4444" />
              </div>

              {/* Workload by Stage */}
              <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                  <Target size={14} className="text-indigo-400" /> Queue Depth by Stage
                </h3>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={stageMetrics} barCategoryGap="30%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="code" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="inQueue" name="In Queue" radius={[4, 4, 0, 0]}>
                      {stageMetrics.map((s, i) => <Cell key={i} fill={s.color} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Workload by County */}
              <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                  <BarChart2 size={14} className="text-sky-400" /> Workload Distribution by County & Stage
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={workloadByCounty} barCategoryGap="20%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="county" tick={{ fill: '#94a3b8', fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11, color: '#94a3b8' }} />
                    {stageMetrics.map(s => (
                      <Bar key={s.code} dataKey={s.code} name={s.code} fill={s.color} stackId="a" />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Officer Workload Table */}
              <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-700/60">
                  <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                    <Users size={14} className="text-indigo-400" /> Officer Queue Load
                  </h3>
                </div>
                <div className="divide-y divide-slate-700/40">
                  {[...officerStats].sort((a, b) => b.pendingQueue - a.pendingQueue).map(o => (
                    <div key={o.id} className="px-5 py-3 flex items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <StatusDot status={o.status} />
                          <span className="text-sm font-medium text-white">{o.name}</span>
                          <StageBadge code={o.stageCode} color={STAGE_COLORS[o.stageCode]} />
                        </div>
                        <div className="text-xs text-slate-400 mt-0.5">{o.role} · {o.county}</div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className={`text-sm font-bold ${o.pendingQueue > 50 ? 'text-red-400' : o.pendingQueue > 25 ? 'text-amber-400' : 'text-emerald-400'}`}>
                            {o.pendingQueue}
                          </div>
                          <div className="text-xs text-slate-400">in queue</div>
                        </div>
                        <div className="w-32">
                          <div className="flex justify-between text-xs text-slate-400 mb-1">
                            <span>Load</span>
                            <span>{Math.round((o.pendingQueue / 70) * 100)}%</span>
                          </div>
                          <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all" style={{
                              width: `${Math.min((o.pendingQueue / 70) * 100, 100)}%`,
                              backgroundColor: o.pendingQueue > 50 ? '#ef4444' : o.pendingQueue > 25 ? '#f59e0b' : '#10b981'
                            }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

        </div>
      </div>
    </AppLayout>
  );
}
